/**
 * Slice 50 — "When you arrive" arrival checklist.
 * Owner side: an ordered list per property (properties.arrival_checklist).
 * Cleaner side: the first screen of the Clean step; answers live in the job's entry_walkthrough_state.
 */

import { supabase } from "@/integrations/supabase/client";
import type { SimpleFlowState } from "@/lib/simpleTurnover";

export interface ArrivalTask {
  id: string;
  text: string;
  required: boolean;
  needsPhoto: boolean;
}

export interface ArrivalAnswer {
  done?: boolean;
  photo_urls?: string[];
}

/** Shown (and used by cleaners) until the owner's first Save. Fully editable. */
export const STARTER_ARRIVAL_TASKS: ArrivalTask[] = [
  { id: "starter-laundry", text: "Start the laundry first, so it runs while you clean", required: true, needsPhoto: false },
  { id: "starter-supplies", text: "Check the cleaning supplies are here", required: true, needsPhoto: false },
  { id: "starter-windows", text: "Open the windows to air the unit out", required: true, needsPhoto: false },
];

type StoredTask = { id?: unknown; text?: unknown; required?: unknown; needs_photo?: unknown };

export function newArrivalTaskId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Read a stored list. null / not an array = never set up. */
export function parseArrivalTasks(raw: unknown): ArrivalTask[] | null {
  if (!Array.isArray(raw)) return null;
  const out: ArrivalTask[] = [];
  for (const item of raw as StoredTask[]) {
    const text = typeof item?.text === "string" ? item.text.trim() : "";
    if (!text) continue;
    out.push({
      id: typeof item.id === "string" && item.id ? item.id : newArrivalTaskId(),
      text,
      required: item.required === true,
      needsPhoto: item.needs_photo === true,
    });
  }
  return out;
}

export function serializeArrivalTasks(tasks: ArrivalTask[]) {
  return tasks
    .map((t) => ({ id: t.id, text: t.text.trim(), required: t.required, needs_photo: t.needsPhoto }))
    .filter((t) => t.text.length > 0);
}

/** The list cleaners use for a property: the owner's saved list, or the starter list if never saved. */
export function effectiveArrivalTasks(raw: unknown): { tasks: ArrivalTask[]; isStarter: boolean } {
  const saved = parseArrivalTasks(raw);
  if (saved === null) return { tasks: STARTER_ARRIVAL_TASKS, isStarter: true };
  return { tasks: saved, isStarter: false };
}

/** Unit tab row subtitle. */
export function arrivalSummary(raw: unknown): string {
  const { tasks, isStarter } = effectiveArrivalTasks(raw);
  if (isStarter) return `Starter list · ${tasks.length} tasks · start of Clean`;
  if (tasks.length === 0) return "No tasks · cleaners skip this";
  const required = tasks.filter((t) => t.required).length;
  return `${tasks.length} task${tasks.length === 1 ? "" : "s"} · ${required} required · start of Clean`;
}

/** Copy of the property's list for a job that is starting. Returns undefined when it could not be read. */
export async function fetchArrivalTasksForJob(propertyId: string): Promise<ReturnType<typeof serializeArrivalTasks> | undefined> {
  try {
    const { data, error } = await supabase
      .from("properties")
      .select("arrival_checklist")
      .eq("id", propertyId)
      .maybeSingle();
    if (error || !data) return undefined;
    const { tasks } = effectiveArrivalTasks((data as unknown as { arrival_checklist: unknown }).arrival_checklist);
    return serializeArrivalTasks(tasks);
  } catch {
    return undefined;
  }
}

export function readArrivalAnswers(state: SimpleFlowState): Record<string, ArrivalAnswer> {
  return (state.arrival_answers as Record<string, ArrivalAnswer> | undefined) ?? {};
}

/** A needs-photo task only counts once at least one photo is attached. */
export function isArrivalTaskDone(task: ArrivalTask, answer: ArrivalAnswer | undefined): boolean {
  if (task.needsPhoto) return (answer?.photo_urls?.length ?? 0) > 0;
  return answer?.done === true;
}

export function requiredTasksLeft(tasks: ArrivalTask[], answers: Record<string, ArrivalAnswer>): number {
  return tasks.filter((t) => t.required && !isArrivalTaskDone(t, answers[t.id])).length;
}

export function arrivalDoneCount(tasks: ArrivalTask[], answers: Record<string, ArrivalAnswer>): number {
  return tasks.filter((t) => isArrivalTaskDone(t, answers[t.id])).length;
}
